/**
 * Order Timing Metrics Tab - Admin Panel
 * Shows order completion time metrics and analysis
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Clock, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  AlertTriangle,
  Timer,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

export default function OrderTimingMetricsTab({ token }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await api.get(`/kds/metrics/timing?days=${days}`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        No metrics data available
      </div>
    );
  }

  const brackets = metrics.by_time_bracket || {};
  const bracketPercent = metrics.by_time_bracket_percent || {};
  const byOrderType = metrics.by_order_type || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Order Timing Metrics</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Order completion time analysis
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchMetrics}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.total_orders}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Orders</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Timer className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {metrics.average_completion_minutes}m
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Avg Completion</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {bracketPercent.under_5_min || 0}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Under 5 min</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {bracketPercent.over_15_min || 0}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Over 15 min</p>
            </div>
          </div>
        </div>
      </div>

      {/* Time Brackets Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            Completion Time Distribution
          </h3>
        </div>
        <div className="p-4 space-y-4">
          {/* Under 5 min */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">Under 5 minutes</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {brackets.under_5_min || 0} orders ({bracketPercent.under_5_min || 0}%)
              </span>
            </div>
            <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${bracketPercent.under_5_min || 0}%` }}
              />
            </div>
          </div>

          {/* 5-10 min */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">5 - 10 minutes</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {brackets['5_to_10_min'] || 0} orders ({bracketPercent['5_to_10_min'] || 0}%)
              </span>
            </div>
            <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-yellow-500 rounded-full transition-all"
                style={{ width: `${bracketPercent['5_to_10_min'] || 0}%` }}
              />
            </div>
          </div>

          {/* 10-15 min */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">10 - 15 minutes</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {brackets['10_to_15_min'] || 0} orders ({bracketPercent['10_to_15_min'] || 0}%)
              </span>
            </div>
            <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-orange-500 rounded-full transition-all"
                style={{ width: `${bracketPercent['10_to_15_min'] || 0}%` }}
              />
            </div>
          </div>

          {/* Over 15 min */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">Over 15 minutes</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {brackets.over_15_min || 0} orders ({bracketPercent.over_15_min || 0}%)
              </span>
            </div>
            <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-red-500 rounded-full transition-all"
                style={{ width: `${bracketPercent.over_15_min || 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* By Order Type */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">By Order Type</h3>
        </div>
        <div className="p-4">
          {Object.keys(byOrderType).length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No data available</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(byOrderType).map(([type, data]) => (
                <div 
                  key={type}
                  className={`p-4 rounded-xl ${
                    type === 'takeaway' 
                      ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
                      : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-medium ${
                      type === 'takeaway' 
                        ? 'text-orange-700 dark:text-orange-300'
                        : 'text-blue-700 dark:text-blue-300'
                    }`}>
                      {type === 'takeaway' ? 'Takeaway' : 'Dine-in'}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {data.count} orders
                    </span>
                  </div>
                  <p className={`text-2xl font-bold ${
                    type === 'takeaway' 
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-blue-600 dark:text-blue-400'
                  }`}>
                    {data.average_minutes}m avg
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800 p-4">
        <h3 className="font-semibold text-green-800 dark:text-green-200 mb-3">Performance Insights</h3>
        <div className="space-y-2 text-sm">
          {metrics.average_completion_minutes <= 10 ? (
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <TrendingUp className="w-4 h-4" />
              <span>Great! Average completion time is under 10 minutes</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <TrendingDown className="w-4 h-4" />
              <span>Average completion time is over 10 minutes - consider optimizing workflow</span>
            </div>
          )}
          
          {(bracketPercent.over_15_min || 0) > 10 && (
            <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
              <AlertTriangle className="w-4 h-4" />
              <span>{bracketPercent.over_15_min}% of orders take over 15 minutes - investigate bottlenecks</span>
            </div>
          )}
          
          {(bracketPercent.under_5_min || 0) > 50 && (
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <CheckCircle2 className="w-4 h-4" />
              <span>Excellent! Over half of orders complete in under 5 minutes</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
